import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * OKR UI State
 *
 * Manages ONLY client-side UI state for OKRs module.
 * Server data is handled by TanStack Query hooks.
 *
 * This store contains:
 * - Active tab selection (lista, novo, analytics)
 * - Active view mode (árvore, lista, timeline)
 * - Dialog/modal state
 * - Selected OKR for editing
 *
 * DO NOT store server data here (OKR progress, key results, etc.)
 */
interface OKRUIStore {
  // ================================
  // TAB NAVIGATION
  // ================================

  /** Current active tab */
  activeTab: 'lista' | 'visualizacoes' | 'cadastro'

  /** Set active tab */
  setActiveTab: (tab: OKRUIStore['activeTab']) => void

  // ================================
  // VIEW MODE
  // ================================

  /** Current view mode for OKR list */
  activeView: 'arvore' | 'lista' | 'timeline'

  /** Set active view mode */
  setActiveView: (view: OKRUIStore['activeView']) => void

  // ================================
  // MODAL STATE
  // ================================

  /** Dialog open state */
  isDialogOpen: boolean

  /** Open dialog */
  openDialog: () => void

  /** Close dialog */
  closeDialog: () => void

  // ================================
  // SELECTION STATE
  // ================================

  /** Selected OKR ID for editing */
  selectedOKRId: string | null

  /** Set selected OKR */
  setSelectedOKRId: (id: string | null) => void

  // ================================
  // UTILITY
  // ================================

  /** Reset all UI state */
  reset: () => void
}

const initialState = {
  activeTab: 'lista' as const,
  activeView: 'arvore' as const,
  isDialogOpen: false,
  selectedOKRId: null,
}

/**
 * OKR UI Store
 *
 * Persisted to localStorage so tab/view preferences survive page refreshes.
 * Modal state is NOT persisted (resets on refresh).
 */
export const useOKRUIStore = create<OKRUIStore>()(
  persist(
    (set) => ({
      ...initialState,

      // Tab navigation
      setActiveTab: (tab) => set({ activeTab: tab }),

      // View mode
      setActiveView: (view) => set({ activeView: view }),

      // Modal state
      openDialog: () => set({ isDialogOpen: true }),
      closeDialog: () => set({ isDialogOpen: false }),

      // Selection state
      setSelectedOKRId: (id) => set({ selectedOKRId: id }),

      // Utility
      reset: () => set(initialState),
    }),
    {
      name: 'okr-ui-store', // localStorage key
      // Only persist tab and view preferences (not modal/selection state)
      partialize: (state) => ({
        activeTab: state.activeTab,
        activeView: state.activeView,
      }),
    }
  )
)
